/**
 * navigation/appShell.tsx
 * The authenticated platform shell. Faithful RN port of design/app.jsx AppShell:
 *   • persistent TopBar
 *   • tenant settings / admin → SubNav (docked ≥900px) or SectionSelect (<900px)
 *   • product areas (crm/catalog/voice) + tenant home → full-width, no SubNav
 *   • role / staff gating → PermissionDenied
 *   • SessionReauthModal overlay
 *
 * In-shell routing is provided by ShellNavProvider (navigation/shellNav).
 */

import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';

import { useTheme } from '../context/themeContext';
import { usePlatform } from '../context/platformContext';
import { useResponsive } from '../utils/responsive';
import { ShellNavProvider, useShellNav, NAV } from './shellNav';
import { NARROW_BP } from '../utils/platformTokens';
import { TopBar, SubNav, SectionSelect, PermissionDenied, SessionReauthModal } from '../screens/shell/chrome';
import { SCREENS } from '../screens/registry';
import type { ShellArea, ShellRoute } from './types';

const PRODUCT_AREAS: ShellArea[] = ['crm', 'catalog', 'voice'];

const ShellBody: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { role, isPlatformAdmin } = usePlatform();
  const { route } = useShellNav();
  const { width } = useResponsive();
  const narrow = width <= NARROW_BP;

  const inAdmin = route.area === 'admin';
  const isProduct = PRODUCT_AREAS.includes(route.area);
  const isHome = route.area === 'tenant' && route.screen === 'home';

  // Resolve the active screen component + permission.
  let Screen = SCREENS[route.screen] || SCREENS.home;
  let allowed = true;
  let fullWidth = false;

  if (inAdmin) {
    allowed = isPlatformAdmin;
    fullWidth = false; // admin uses the subnav layout
  } else if (isProduct) {
    Screen = SCREENS[route.area] || SCREENS.home;
    allowed = true;
    fullWidth = true; // products render full-width, no settings subnav
  } else {
    const navItem = NAV.find((n) => n.id === route.screen);
    allowed = route.screen === 'home' || !navItem || navItem.roles.includes(role);
    fullWidth = isHome;
  }

  return (
    <View style={[styles.root, { backgroundColor: c.paper }]}>
      <TopBar />
      {fullWidth ? (
        allowed ? <Screen /> : <PermissionDenied />
      ) : (
        <View style={styles.layout}>
          {!narrow && <SubNav />}
          <View style={{ flex: 1, minWidth: 0 }}>
            {narrow && <SectionSelect />}
            {allowed ? <Screen /> : <PermissionDenied />}
          </View>
        </View>
      )}
      <SessionReauthModal />
    </View>
  );
};

export default function AppShell({
  onSignOut,
  initialRoute,
}: {
  onSignOut: () => void;
  /** Optional first route — e.g. land on Settings right after onboarding. */
  initialRoute?: ShellRoute;
}) {
  return (
    <ShellNavProvider signOut={onSignOut} initial={initialRoute}>
      <ShellBody />
    </ShellNavProvider>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}) },
  layout: { flex: 1, flexDirection: 'row', alignItems: 'stretch' },
});
